import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type StatsType = "ticket" | "event" | "user" | "campaign" | "partner-codes" | "openai-usage";

export const useAdminStatsGeneration = () => {
  const queryClient = useQueryClient();
  const [generatingTypes, setGeneratingTypes] = useState<Record<StatsType, boolean>>({
    ticket: false,
    event: false,
    user: false,
    campaign: false,
    "partner-codes": false,
    "openai-usage": false,
  });

  const generateStats = async (type: StatsType) => {
    setGeneratingTypes(prev => ({ ...prev, [type]: true }));
    
    try {
      const { data, error } = await supabase.functions.invoke(`generate-${type}-stats`);
      
      if (error) throw error;
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} stats regenerated successfully`);
      
      // Invalidate the cache query to refetch
      queryClient.invalidateQueries({ queryKey: [`${type}-stats-cache`] });
      
      return data;
    } catch (error: any) {
      console.error(`Error generating ${type} stats:`, error);
      toast.error(`Failed to generate ${type} stats: ${error.message}`);
      throw error;
    } finally {
      setGeneratingTypes(prev => ({ ...prev, [type]: false }));
    }
  };

  // Silent background refresh - no toast notifications
  const generateStatsBackground = useCallback(async (type: StatsType) => {
    setGeneratingTypes(prev => ({ ...prev, [type]: true }));
    
    try {
      const { data, error } = await supabase.functions.invoke(`generate-${type}-stats`);
      
      if (error) throw error;
      
      // Silently invalidate cache - no toast notification
      queryClient.invalidateQueries({ queryKey: [`${type}-stats-cache`] });
      
      return data;
    } catch (error: any) {
      console.error(`Background refresh failed for ${type} stats:`, error);
      // No toast on background failure - silent fail
    } finally {
      setGeneratingTypes(prev => ({ ...prev, [type]: false }));
    }
  }, [queryClient]);

  const generateAllStats = async () => {
    const types: StatsType[] = ["ticket", "event", "user", "campaign", "partner-codes"];
    
    try {
      await Promise.all(types.map(type => generateStats(type)));
      toast.success("All stats regenerated successfully");
    } catch (error) {
      // Individual errors are already handled in generateStats
    }
  };

  const isGenerating = useCallback((type: StatsType) => generatingTypes[type], [generatingTypes]);
  const isAnyGenerating = Object.values(generatingTypes).some(Boolean);

  return {
    generateStats,
    generateStatsBackground,
    generateAllStats,
    isGenerating,
    isAnyGenerating,
  };
};
